import { FC, JSX, useContext, useEffect, useMemo, useState } from "react";
import { ZipProjectContext } from "./ZipProjectContext";
import { ProjectPerspective } from "../enumerators";
import { isZipCodeInputValid } from "@app/shared/utility/isZipCodeInputValid";
import { ZipAnalysisSearchSelections } from "./interfaces/zipAnalysisSearchSelections";
import { ZipProjectActionsEnum } from "./interfaces";
import SingleSelectDropdown from "@app/shared/checkbox-dropdown/SingleSelectDropdown";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";

export const ZipProjectFilters: FC = () => {
  const projectContext = useContext(ZipProjectContext);
  const { state, dispatch } = projectContext;
  const [citySuggestions, setCitySuggestions] = useState<JSX.Element[]>([]);
  const [zipCodeSuggestions, setZipCodeSuggestions] = useState<JSX.Element[]>([]);

  const hasUnsavedChanges = useMemo(() => {
    return (
      state.filterSelections.city !== state.filterSelectionsTemp.city
      || state.filterSelections.county.key !== state.filterSelectionsTemp.county.key
      || state.filterSelections.metOverall.key !== state.filterSelectionsTemp.metOverall.key
      || state.filterSelections.metPassPercent.key !== state.filterSelectionsTemp.metPassPercent.key
      || state.filterSelections.specialtyGroupCode.key !== state.filterSelectionsTemp.specialtyGroupCode.key
      || state.filterSelections.state.key !== state.filterSelectionsTemp.state.key
      || state.filterSelections.zipClass.key !== state.filterSelectionsTemp.zipClass.key
      || state.filterSelections.zipCode !== state.filterSelectionsTemp.zipCode
    );
  }, [state.filterSelections, state.filterSelectionsTemp]);

  const handleFilterChange = (selectedItem: DropdownItem<string> | string, filter: keyof ZipAnalysisSearchSelections) => {
    dispatch({
      payload: {
        filterSelectionsTemp: {
          ...state.filterSelectionsTemp,
          [filter]: selectedItem,
        }
      },
      type: ZipProjectActionsEnum.ChangeFilter,
    })
  };

  useEffect(() => {
    const newCitySuggestions = filterTextFieldSuggestions("city");
    setCitySuggestions(newCitySuggestions);
  }, [state.filterSelectionsTemp.city, state.filterOptions.city]);

  useEffect(() => {
    const newZipCodeSuggestions = filterTextFieldSuggestions("zipCode");
    setZipCodeSuggestions(newZipCodeSuggestions);
  }, [state.filterSelectionsTemp.zipCode, state.filterOptions.zipCode]);

  const filterTextFieldSuggestions = (key: "city" | "zipCode") => {
    if (!state.filterOptions[key]) {
      return;
    }

    const itemStartsWith: string[] = [];
    const itemIncludes: string[] = [];
    state.filterOptions[key].forEach((item) => {
      if (item.toLowerCase().startsWith(state.filterSelectionsTemp[key].toLowerCase())) {
        itemStartsWith.push(item);
      } else if (item.toLowerCase().includes((state.filterSelectionsTemp[key].toLowerCase()))) {
        itemIncludes.push(item);
      }
    });

    const newSuggestions = [...itemStartsWith, ...itemIncludes]
      .slice(0, 19)
      .map((o) => <option key={o} value={o} />);

    return newSuggestions;
  }

  return (
    <div className="card w-100">
      <div className="card-body">
        <div className="row justify-content-start">
          <SingleSelectDropdown
            label="Specialty"
            containerClassName="col"
            items={state.filterOptions.specialtyGroupCode}
            testId="zip-view-by-all__filter--specialty"
            onSelectionChanged={(selectedItem) => handleFilterChange(selectedItem, "specialtyGroupCode")}
            selectedItem={state.filterSelectionsTemp.specialtyGroupCode}
            disabled={state.loading}
          />
          <SingleSelectDropdown
            label="Classification"
            containerClassName="col"
            items={state.filterOptions.zipClass}
            testId="zip-view-by-all__filter--zip-class"
            onSelectionChanged={(selectedItem) => handleFilterChange(selectedItem, "zipClass")}
            selectedItem={state.filterSelectionsTemp.zipClass}
            disabled={state.loading}
          />

          {state.perspective === ProjectPerspective.ByZipCode && (
            <>
              <SingleSelectDropdown
                label="State"
                containerClassName="col"
                items={state.filterOptions.state}
                testId="zip-view-by-all__filter--state"
                onSelectionChanged={(selectedItem) => handleFilterChange(selectedItem, "state")}
                selectedItem={state.filterSelectionsTemp.state}
                disabled={state.loading}
              />
              <QESTextInput
                label="City"
                containerClassName="col"
                testId="zip-view-by-all__filter--city"
                onChange={(e) => {
                  handleFilterChange(e.target.value, "city");
                }}
                value={state.filterSelectionsTemp.city}
                disabled={state.loading}
                list="cities"
              />
              <datalist id="cities">
                {citySuggestions}
              </datalist>
              <SingleSelectDropdown
                label="Primary County"
                containerClassName="col"
                items={state.filterOptions.county}
                testId="zip-view-by-all__filter--county"
                onSelectionChanged={(selectedItem) => handleFilterChange(selectedItem, "county")}
                selectedItem={state.filterSelectionsTemp.county}
                disabled={state.loading}
              />
              <QESTextInput
                label="Zip Code"
                containerClassName="col"
                testId="zip-view-by-all__filter--zip"
                onChange={(e) => {
                  if (!isZipCodeInputValid(e.target.value)) {
                    return;
                  }
                  handleFilterChange(e.target.value, "zipCode");
                }}
                value={state.filterSelectionsTemp.zipCode}
                // should only allow digits, and should be a min and max length of 5
                // this doesn't prevent any user inputs, but could potentially be used for validation
                // see https://developer.mozilla.org/en-US/docs/Web/API/ValidityState/patternMismatch
                pattern="^\d{5,5}$"
                disabled={state.loading}
                list="zip-codes"
              />
              <datalist id="zip-codes">
                {zipCodeSuggestions}
              </datalist>
            </>
          )}

          {state.perspective === ProjectPerspective.ByZipClass ? (
            <SingleSelectDropdown
              label="Met Overall"
              containerClassName="col"
              items={state.filterOptions.metOverall}
              testId="zip-view-by-all__filter--met-overall"
              onSelectionChanged={(selectedItem) => handleFilterChange(selectedItem, "metOverall")}
              selectedItem={state.filterSelectionsTemp.metOverall}
              disabled={state.loading}
            />
          ) : (
            <SingleSelectDropdown
              label="Met Overall"
              containerClassName="col"
              items={state.filterOptions.metPassPercent}
              testId="zip-view-by-all__filter--met-overall"
              onSelectionChanged={(selectedItem) => handleFilterChange(selectedItem, "metPassPercent")}
              selectedItem={state.filterSelectionsTemp.metPassPercent}
              disabled={state.loading}
            />
          )}

          <QESButton
            className="px-2 align-self-end mb-2 py-0 mb-0 col-auto"
            qesType="secondary"
            qesTestId="zip-view-by-all__filter-submit"
            onClick={projectContext.onApplyFilters}
            disabled={state.loading || !hasUnsavedChanges}
          >
            Apply
          </QESButton>
          <QESButton
            className="py-0 px-2 align-self-end mb-2 me-2 col-auto"
            qesType="secondary-danger"
            qesTestId="zip-view-by-all__filter-reset"
            onClick={projectContext.onResetFilters}
            disabled={state.loading}
          >
            Reset
          </QESButton>
        </div>
      </div>
    </div>
  )
}
